import { SelectNavigationSidebarDisplayMode } from '../components/Showcase/CustomRenderer/SelectNavigationSidebarDisplayMode';
import type { ShowcaseSpec } from '../components/Showcase/types';

/** Showcase names, sorted by order of appearance, most recent first */
export const ShowcaseName = {
  v255UpdatedNavigationSidebar: 'v2.55.0 Updated navigation sidebar',
  v256TradingSpaces: 'v2.56.0 Trading spaces',
} as const;
export type ShowcaseName = (typeof ShowcaseName)[keyof typeof ShowcaseName];

export const showcases: Record<ShowcaseName, ShowcaseSpec> = {
  [ShowcaseName.v256TradingSpaces]: {
    releasedInVersion: '2.56.0',
    width: '640px',
    height: '600px',
    title: 'Trading spaces',
    requiredFeatureFlag: 'enableTradingSpaces',
    steps: [
      {
        targetSelector: '[data-testid="sidebar-submenu-wrapper"]',
        learnMoreUrl: 'https://kb.talostrading.com/principal-trading/flexible-ui',
        title: 'Introducing Trading Spaces',
        description: `We've added a new way for users to organise their trading screens. You can now:
  - Add multiple spaces with different content and layout
  - Share spaces to other users in your organization
  - Setup default spaces for new users`,
        imageDark: '/assets/showcase/spaces-dark-1.png',
        imageLight: '/assets/showcase/spaces-light-1.png',
      },
      {
        targetSelector: '[data-testid="new-space-button"]',
        learnMoreUrl: 'https://kb.talostrading.com/principal-trading/flexible-ui',
        title: 'Adding Spaces',
        description: `You can add new spaces tailored for different types of trading. You can start from an existing template or create a copy of your current space.`,
        imageDark: '/assets/showcase/spaces-dark-2.png',
        imageLight: '/assets/showcase/spaces-light-2.png',
      },
      {
        targetSelector: '[data-testid="create-template-button"]',
        learnMoreUrl: 'https://kb.talostrading.com/principal-trading/flexible-ui',
        title: 'Sharing Spaces',
        description: `You can share your space with other users in your organization by creating new templates and sharing them with specific user roles, user groups or individual users.`,
        imageDark: '/assets/showcase/spaces-dark-3.png',
        imageLight: '/assets/showcase/spaces-light-3.png',
      },
      {
        targetSelector: '[data-testid="sidebar-submenu-items"]',
        learnMoreUrl: 'https://kb.talostrading.com/principal-trading/flexible-ui',
        title: 'Default Spaces',
        description: `Admin users can configure templates to be included as the default setup for new users. Users with the Spaces Admin role can also add default spaces for all users in the organization. Default spaces are configured to appear based on the user’s role or user group.`,
        imageDark: '/assets/showcase/spaces-dark-4.png',
        imageLight: '/assets/showcase/spaces-light-4.png',
      },
    ],
  },
  [ShowcaseName.v255UpdatedNavigationSidebar]: {
    releasedInVersion: '2.55.0',
    width: '640px',
    height: '520px',
    title: 'Updated Navigation Sidebar',
    steps: [
      {
        targetSelector: '[data-testid="sidebar-menu"]',
        learnMoreUrl: 'https://kb.talostrading.com/principal-trading/navigating-trading-ui',
        title: 'Updates to Our Main Navigation',
        description: `We've updated the navigation to make moving between products easier and more space-efficient.
  - Admin pages are separate from Settings and are now located in the main menu
  - User settings are now located in the bottom user popout
`,
        customRenderer: SelectNavigationSidebarDisplayMode,
      },
      {
        targetSelector: '[data-testid="sidebar-nav-item"][href="/admin"]',
        learnMoreUrl: 'https://kb.talostrading.com/principal-trading/navigating-trading-ui',
        title: 'Centralized Admin Settings',
        imageDark: '/assets/showcase/wn-sidebar-admin-dark.png',
        imageLight: '/assets/showcase/wn-sidebar-admin-light.png',
        description: `A new section for admin settings has been added to the main menu. It contains all the organization wide setup that were previously located in the Settings menu.`,
      },
      {
        targetSelector: '[data-testid="sidebar-user-profile-item"]',
        learnMoreUrl: 'https://kb.talostrading.com/principal-trading/navigating-trading-ui',
        title: 'User Settings Relocated',
        imageDark: '/assets/showcase/wn-sidebar-user-settings-dark.png',
        imageLight: '/assets/showcase/wn-sidebar-user-settings-light.png',
        description: `All user-related settings and preferences are now located in the bottom user popout, making them easier to find and access.`,
      },
      {
        targetSelector: '[data-testid="sidebar-search-item"]',
        learnMoreUrl: 'https://kb.talostrading.com/principal-trading/navigating-trading-ui/power-search',
        title: 'Improved Power Search',
        imageDark: '/assets/showcase/wn-sidebar-power-search-dark.png',
        imageLight: '/assets/showcase/wn-sidebar-power-search-light.png',
        description: `You can now navigate to any page using the power search bar. Just type the name of the page you want to navigate to and press enter.`,
      },
    ],
  },
};
